/**
 * Permission System — Core Domain Types
 *
 * Defines the fundamental types for Relationship-Based Access Control (ReBAC).
 *
 * Pattern: const objects are the source of truth.
 * - Named access: `ResourceType.AGENT`, `Relation.OWNER`, `SubjectType.USER`
 * - Union types derived from const objects (no duplication)
 * - Tuples for Zod: `z.enum(RESOURCE_TYPES)`
 *
 * Adding a new resource: add to the ResourceType object — union, array, and Zod
 * schemas all update automatically.
 *
 * Frontend-safe: zero Prisma runtime dependency.
 */
/*
 * Every `export const X` here is paired with an `export type X` of the same name.
 * That is the documented pattern above, not an accident: the const is the runtime
 * source of truth and the type is its compile-time projection, so `ResourceType`
 * reads the same in a value position and a type position. TypeScript merges the
 * two declarations by design; `no-redeclare` only sees the collision.
 */
import { z } from "zod";

import type { PermissionSetSurfaceValue } from "../roles/permission-set-surface.types";

// ============================================================================
// Resource Types
// ============================================================================

export const ResourceType = {
  AGENT: "agent",
  WORKFLOW: "workflow",
  KNOWLEDGE: "knowledge",
  CREDENTIAL: "credential",
  /**
   * A scoped delegation of one CREDENTIAL — the object an owner hands to a
   * machine caller so it can act with that credential without holding it.
   *
   * It carries a system-level CLOSED default for the same reason VIBE_APP does,
   * arrived at from the other direction: a card is not reachable from the public
   * internet, but the SUBJECT that reaches it is a `PublicApiKey`, and
   * `ResourcePermissionGuard` waves every API-key request straight through by
   * explicit product decision. The grant rows ARE the per-key scope claims that
   * decision defers to, so a card that is org-open by omission makes every key
   * in the org a skeleton key for every credential in it — see
   * {@link SYSTEM_DEFAULT_RESOURCE_VISIBILITY}.
   */
  ACCESS_CARD: "access_card",
  TEMPLATE: "template",
  DOCUMENT: "document",
  DEPLOYMENT: "deployment",
  /** Layer 1: feature catalog access (no grants = everyone has access) */
  FEATURE: "feature",
  /**
   * A deployed Vibe app, gated at the tenant edge rather than by a Nest guard.
   * Unlike every other resource type this one is a PUBLIC-INTERNET boundary: the
   * subject is whoever pointed a browser at `<app>.<tenant>.gpt.nexus`, not an
   * authenticated console caller. That is why it carries a system-level CLOSED
   * default — see {@link SYSTEM_DEFAULT_RESOURCE_VISIBILITY}.
   */
  VIBE_APP: "vibe_app",
  /**
   * A drive/file Workspace. Defaults OPEN like every other non-pinned type —
   * unlike VIBE_APP this is a console-only, authenticated-caller surface, not a
   * public-internet boundary, so no {@link SYSTEM_DEFAULT_RESOURCE_VISIBILITY}
   * pin applies. Narrowing comes from `RoleWorkspaceGrant` (a live-resolved M:N
   * grant, the same shape as `RoleCollectionGrant` for KNOWLEDGE) rather than
   * from materialized `ResourcePermission` rows, and it is an ALLOW-LIST over
   * the caller's own placement — a Workspace with zero grants anywhere is
   * reached by callers placed in no Role and by no Role-placed caller; the
   * moment it has at least one grant, it is reached by the Role(s) holding it
   * and by nobody else.
   */
  WORKSPACE: "workspace",
  /**
   * One Track — the unit of work an agent loop plans, claims and reports
   * against.
   *
   * It carries a system-level CLOSED default, and the argument is the ACCESS
   * CARD one rather than the VIBE_APP one: a Track is not reachable from the
   * public internet, but the caller that drives one is overwhelmingly a
   * `PublicApiKey`. `ResourcePermissionGuard` admits every API-key request by
   * explicit product decision and defers per-key scoping to these grant rows,
   * so a type that is org-open by omission would ship today's whole-org
   * behaviour wearing a permission system — see
   * {@link SYSTEM_DEFAULT_RESOURCE_VISIBILITY}.
   *
   * 🔴 REGISTERING THE TYPE IS NOT GATING A ROUTE, AND THE PIN IS INERT UNTIL
   * ONE IS GATED. No tracks controller carries `@UseGuards`, `@RequirePermission`
   * or a `canAccess` call; every one of them authorizes on
   * `@CurrentOrganizationId()` alone. Nothing evaluates this pin yet, and no
   * create path writes a `ResourcePermission` row for a Track — so the lane that
   * first gates a tracks route inherits a corpus of zero-grant Tracks, which
   * under CLOSED are reachable by an org admin (`PermissionCheckerService`
   * short-circuits on `isOrgAdmin`) and by nobody else. That is a seeding
   * decision to take BEFORE the first gate lands, not after.
   */
  TRACK: "track"
} as const;

export type ResourceType = (typeof ResourceType)[keyof typeof ResourceType];

/** Tuple for Zod: z.enum(RESOURCE_TYPES) */
export const RESOURCE_TYPES = Object.values(ResourceType) as [ResourceType, ...ResourceType[]];

/**
 * The resource types the generic Permissions API refuses OUTRIGHT, because
 * their access is narrowed exclusively by Role grants.
 *
 * Neither type's create path calls `initializeResourcePermissions`, so neither
 * ever carries a `ResourcePermission` row: KNOWLEDGE (a Collection) is narrowed
 * through `RoleCollectionGrant` and WORKSPACE through `RoleWorkspaceGrant`, both
 * live-resolved M:N tables. Routing either through the generic grant/revoke/read
 * routes runs the base ReBAC check, which finds zero rows for a type that never
 * writes any — and on a CLOSED default silently denies every one of them
 * regardless of the Role grant that does reach it.
 *
 * The backend has refused both since `assertNotRoleGrantOnlyResourceType`
 * landed. This list is what stops the request schemas built on
 * {@link GENERIC_GRANT_RESOURCE_TYPES} still ADVERTISING them — a caller reading
 * `resourceType`'s enum, in `--help`, in the OpenAPI document or in the SDK's
 * types, was being offered a value the route would always reject, and the only
 * way to learn otherwise was to send it and read the 400 (NEX-3761).
 */
export const ROLE_GRANT_ONLY_RESOURCE_TYPES = [
  ResourceType.KNOWLEDGE,
  ResourceType.WORKSPACE
] as const;

/** A `ResourceType` the generic Permissions API refuses outright. */
export type RoleGrantOnlyResourceType = (typeof ROLE_GRANT_ONLY_RESOURCE_TYPES)[number];

const ROLE_GRANT_ONLY_RESOURCE_TYPE_SET: ReadonlySet<ResourceType> = new Set(
  ROLE_GRANT_ONLY_RESOURCE_TYPES
);

/**
 * The set membership test, as a TYPE GUARD rather than a bare `Set.has`.
 *
 * `Set.has` returns a boolean and narrows nothing, so a caller keying anything
 * per member — the route map below, the backend's error message — would need a
 * cast, which is exactly the escape that lets a third member ship with no route
 * to name. Narrowing here makes those records exhaustive at the compiler.
 */
export function isRoleGrantOnlyResourceType(
  resourceType: ResourceType
): resourceType is RoleGrantOnlyResourceType {
  return ROLE_GRANT_ONLY_RESOURCE_TYPE_SET.has(resourceType);
}

/** Where each refused type's access IS written and read. */
const ROLE_GRANT_ROUTE: Record<RoleGrantOnlyResourceType, string> = {
  [ResourceType.KNOWLEDGE]: "/roles/:roleId/collection-grants",
  [ResourceType.WORKSPACE]: "/roles/:roleId/workspace-grants"
};

/**
 * ONE sentence, read by two surfaces: the v1 request schemas reject the value
 * with it, and the backend's `RoleGrantOnlyResourceTypeError` throws it for
 * every caller that reaches the domain guard without passing a v1 schema first
 * (the dashboard controllers). Two hand-written copies would drift, and a
 * refusal that names a different route on each surface is worse than either.
 *
 * 🚨 IT NAMES THE ROUTE THAT ANSWERS, AND SAYS WHICH QUESTION HAS NO ROUTE AT
 * ALL. It used to say "use the /collections or /workspaces endpoints instead" —
 * those manage the RESOURCE, not access to it, so a caller asking who can reach
 * a Collection was sent to a door that does not open. The second sentence is a
 * measured absence rather than a hedge: the only reverse direction in the
 * contract is per-ROLE, so "which Roles reach this resource" is answerable only
 * by enumerating Roles.
 */
export function roleGrantOnlyResourceTypeMessage(resourceType: RoleGrantOnlyResourceType): string {
  return (
    `${resourceType} access is managed exclusively through Role grants, never through the ` +
    `generic Permissions API — write and read them at ${ROLE_GRANT_ROUTE[resourceType]}. No route ` +
    `lists the Roles reaching one ${resourceType}: enumerate the organization's Roles and read ` +
    `each one's grants.`
  );
}

/** A `ResourceType` a generic Permissions API call can actually name. */
export type GenericGrantResourceType = Exclude<ResourceType, RoleGrantOnlyResourceType>;

/**
 * The resource types the generic Permissions API accepts.
 *
 * DERIVED from the refusal list rather than listed, for the same reason
 * {@link OVERRIDABLE_RESOURCE_TYPES} is derived from the pin map: a hand-written
 * copy would let a request schema keep offering a type the guard had started
 * throwing on, and an enum that advertises a value the route always refuses is
 * the defect this exists to close.
 */
export const GENERIC_GRANT_RESOURCE_TYPES = RESOURCE_TYPES.filter(
  (resourceType): resourceType is GenericGrantResourceType =>
    !isRoleGrantOnlyResourceType(resourceType)
) as [GenericGrantResourceType, ...GenericGrantResourceType[]];

// ============================================================================
// Subject Types
// ============================================================================

export const SubjectType = {
  USER: "user",
  GROUP: "group",
  ORGANIZATION: "organization",
  /**
   * A specific API key (`PublicApiKey.id`) held as a grantable principal in its
   * own right — the machine counterpart of a USER.
   *
   * This exists because `ResourcePermissionGuard` deliberately waves every
   * API-key request straight through (see its own comment: "introduce per-key
   * scope claims instead of removing this guard"). That blanket bypass is right
   * for org-scoped console automation and catastrophic at an internet-facing
   * edge, where it would let any key in the org open every private Vibe app.
   * Naming the key as a subject IS those per-key scope claims: a key reaches a
   * resource when, and only when, a row says so.
   */
  API_KEY: "api_key",
  /**
   * A Role (NEX-2514) — the caller is placed in a Role that has been granted
   * access: its owner, or a `user` seat in any of its permission sets. Resolved
   * DYNAMICALLY per request from `Role.ownerUserId` + `RoleGroupMember`, the same
   * shape GROUP is resolved from `UserGroup` — no `ResourcePermission` row is ever
   * written for "user is IN role R" itself.
   *
   * What DOES get written: one `ResourcePermission(subjectType=ROLE,
   * subjectId=<roleId>)` row per resource the Role contains (via
   * `RoleResource`), not one per member — see `RolePermissionSyncService`.
   * A resource entering a Role also loses its `ORGANIZATION`-subject
   * companion row, so it stops being org-default-open the moment it is
   * role-owned; every sibling resource of the same type is untouched.
   */
  ROLE: "role"
} as const;

export type SubjectType = (typeof SubjectType)[keyof typeof SubjectType];

/** Tuple for Zod: z.enum(SUBJECT_TYPES) */
export const SUBJECT_TYPES = Object.values(SubjectType) as [SubjectType, ...SubjectType[]];

// ============================================================================
// Relations
// ============================================================================

/**
 * Hierarchy: OWNER (3) > EDITOR (2) > VIEWER (1)
 * Delegation rule: you can only grant a relation ≤ your own.
 */
export const Relation = {
  OWNER: "owner",
  EDITOR: "editor",
  VIEWER: "viewer"
} as const;

export type Relation = (typeof Relation)[keyof typeof Relation];

/** Tuple for Zod: z.enum(RELATIONS) */
export const RELATIONS = Object.values(Relation) as [Relation, ...Relation[]];

/** All relations in descending order of authority. Use for dropdowns and iteration. */
export const RELATIONS_ORDERED = [Relation.OWNER, Relation.EDITOR, Relation.VIEWER] as const;

/**
 * Runtime membership check for a stored relation.
 *
 * Every column holding one — `ResourcePermission.relation`,
 * `RoleGroup.resourceRelation` — is TEXT rather than a database enum, the same
 * choice the surface and capability catalogs make. So a row can hold a value
 * this build does not know, and turning stored text into a `Relation` needs a
 * guard rather than a cast.
 *
 * The hierarchy is STRUCTURAL — owner 3 > editor 2 > viewer 1 is compared all
 * over the permission layer — so an unrecognised value has no defensible
 * position in it. Callers degrade to the least-granting reading (`null`, or a
 * denial), never to a middle rank.
 */
export const isRelation = (value: unknown): value is Relation =>
  typeof value === "string" && (RELATIONS as readonly string[]).includes(value);

/**
 * Numeric hierarchy for relation comparison.
 * Higher value = broader permissions.
 */
export const RELATION_HIERARCHY: Record<Relation, number> = {
  [Relation.OWNER]: 3,
  [Relation.EDITOR]: 2,
  [Relation.VIEWER]: 1
};

/**
 * The stronger of two relations, with `null` meaning "confers nothing".
 *
 * Lives beside {@link RELATION_HIERARCHY} because it is the only correct way to
 * read it for this question, and because the comparison was hand-rolled at
 * every call site: eight files spell `RELATION_HIERARCHY[a] >= RELATION_HIERARCHY[b]`
 * inline today, in both directions and with both operators. A named function is
 * what makes "which of these two wins" reviewable at a glance instead of a
 * subscript expression the reader has to evaluate.
 *
 * 🚨 `null` is ABSENCE, never a rank. It loses to every real relation rather
 * than sorting below `VIEWER`, so `highestRelation(null, x)` is `x` for every
 * `x` including `null` itself. A hierarchy lookup on `null` would be
 * `undefined`, and `undefined >= n` is `false` in both directions — which is
 * how a "pick the highest" written as a bare comparison silently answers with
 * the WRONG side rather than throwing.
 *
 * Frontend-safe: no Prisma runtime dependency.
 */
export const highestRelation = (left: Relation | null, right: Relation | null): Relation | null => {
  if (left === null) return right;
  if (right === null) return left;
  return RELATION_HIERARCHY[left] >= RELATION_HIERARCHY[right] ? left : right;
};

// ============================================================================
// Permission Subjects
// ============================================================================

/**
 * One Role reached through ONE `RoleGroup` the caller sits in — the permission
 * set's ceiling (`relation`) and the surfaces it is narrowed to.
 *
 * A caller may hold SEVERAL grants on the same `roleId`: a user row in the
 * Maintainers group, a group row in a custom group, an organization-wide row.
 * They are kept apart rather than merged so the resolved answer stays derivable
 * — merging two grants would have to invent a rule for combining a `viewer`
 * with `['inbox']` and an `editor` with `['*']`, and any such rule is a second
 * mechanism deciding access.
 *
 * `relation === null` is a MEANING, never a missing value: the group confers no
 * resource access at all (a capability-only group). It is not "default to
 * viewer" — see the `RoleGroup.resourceRelation` column comment.
 *
 * `surfaces` is a STRICT ALLOW-LIST: an EMPTY array reaches NOTHING, and one
 * entry of `'*'` reaches everything. Never test it with `.length === 0` at a
 * call site — use {@link roleGrantCoversSurface}, which is the single
 * definition of that semantic.
 */
export interface RoleGrant {
  readonly roleId: string;
  readonly relation: Relation | null;
  readonly surfaces: readonly PermissionSetSurfaceValue[];
}

/**
 * All subjects belonging to the current user.
 * Resolved once per request by the guard — no hidden queries inside the checker.
 */
export interface PermissionSubjects {
  readonly userId: string;
  readonly userGroupIds: readonly string[];
  readonly organizationId: string;
  /**
   * Roles (NEX-2514) the caller belongs to — owner via `Role.ownerUserId`, or a
   * `subjectType=user` `RoleGroupMember` seat in any of the Role's permission
   * sets. Resolved live per request, same as `userGroupIds`; membership changes
   * need no permission-row writes.
   *
   * 🚨 A `group` OR `organization` SEAT IS NOT MEMBERSHIP, AND THAT ASYMMETRY IS
   * THE POINT OF THIS FIELD EXISTING BESIDE `roleGrants`. `RoleGroupMember`
   * admits all three subject kinds, and a single organization-subject row names
   * every user in the tenant — so counting one here would place an entire
   * organization in a Role nobody added them to. Only the `user` kind is read.
   * The other two land in `roleGrants` and nowhere else, which is the
   * "a grant and no membership" case documented on that field below.
   *
   * This field is the SEATING, because the seat is the membership and carries
   * the caller's permission set.
   *
   * 🔴 EMPTY IS NOT "NO EXTRA ACCESS". `PermissionCheckerService` treats an empty
   * list as UNPLACED and hands that caller the pre-Roles wide-open path — every
   * Collection and Workspace no Role has claimed. So a bug that merely FAILS to
   * populate this field grants access as well as removing it. Any code
   * constructing a `PermissionSubjects` must answer this field from the real
   * membership read, never default it to `[]` to get past a type error.
   */
  readonly roleIds: readonly string[];
  /**
   * The caller's `RoleGroup` memberships, resolved live per request from
   * `RoleGroupMember` → `RoleGroup` (+ its `RoleGroupSurface` rows).
   *
   * ALONGSIDE `roleIds`, not instead of it, and the two answer different
   * questions. `roleIds` says WHICH Roles speak for the caller in the
   * `ResourcePermission` table — a `SubjectType.ROLE` row is written per
   * (resource, Role), so membership alone is the whole of that check.
   * `roleGrants` says, for each Role, WHAT the caller's permission set inside
   * it permits: a relation ceiling and a surface allow-list. A caller can hold
   * a `roleId` and no grant (their groups confer nothing) or a grant and no
   * membership (an organization-wide `RoleGroupMember` row).
   *
   * REQUIRED, deliberately. An optional field would let a construction site
   * that forgot it resolve to "no grants" — which reads as a clean deny while
   * being an unasked question. Every site must state its answer, and `[]` is a
   * legitimate one for a machine caller.
   *
   * Empty whenever `ROLES` is off for the organization, so the flag being off
   * is byte-identical to the pre-permission-set behaviour.
   */
  readonly roleGrants: readonly RoleGrant[];
  /**
   * The `PublicApiKey.id` the caller presented, when the caller is a machine
   * rather than a person. Absent for console/session callers.
   *
   * Deliberately NOT the key itself or its hash: a subject id lands in a
   * queryable TEXT column and in audit rows, so it must be an identifier that
   * is safe to log. Resolve the key to its row first, then pass the id.
   */
  readonly apiKeyId?: string;
}

// ============================================================================
// Permission Record (domain entity — no Prisma dependency)
// ============================================================================

export interface ResourcePermission {
  readonly id: string;
  readonly organizationId: string;
  readonly resourceType: ResourceType;
  /** UUID for specific resources, '*' for wildcard (god-mode groups only) */
  readonly resourceId: string;
  readonly subjectType: SubjectType;
  readonly subjectId: string;
  readonly relation: Relation;
  readonly grantedByUserId: string | null;
  readonly grantedByPermissionId: string | null;
  readonly createdAt: Date;
  readonly updatedAt: Date;
}

// ============================================================================
// Resource Visibility Default (org-level policy)
// ============================================================================

/**
 * Org-level policy that controls what happens when a resource has
 * zero permission records (i.e. has never been explicitly shared or restricted).
 *
 * - OPEN:   all org members can access the resource (default, backward-compatible)
 * - CLOSED: only the creator + org:admin can access; others need an explicit grant
 *
 * Stored as TEXT in Organization.defaultResourceVisibility.
 * Changing the setting takes effect immediately for new resources.
 * Existing resources with no permission records respect the new setting on the next request.
 */
export const ResourceVisibility = {
  OPEN: "open",
  CLOSED: "closed"
} as const;

export type ResourceVisibility = (typeof ResourceVisibility)[keyof typeof ResourceVisibility];

export const RESOURCE_VISIBILITY_VALUES = Object.values(ResourceVisibility) as [
  ResourceVisibility,
  ...ResourceVisibility[]
];

// ============================================================================
// Unlisted reach (who an access list does NOT name)
// ============================================================================

/**
 * Who reaches a resource BEYOND the grants its access list names.
 *
 * An access list is read to answer "who can reach this?", and the grant rows
 * alone cannot answer it: a resource with no rows at all is reachable by the
 * whole organization under OPEN visibility, and a resource of a type carrying a
 * wildcard grant is reachable through a row that names the TYPE rather than the
 * resource. Both render as the same empty array, and an auditor reading that
 * array concludes the resource is unshared.
 *
 * So the reach travels beside the rows rather than being inferred from their
 * count. The three values are exhaustive over the question and are ordered
 * widest-first, which is also the order the server resolves them in — an
 * organization-open resource is reported as such even when a wildcard grant
 * also reaches it, because the wider fact is the one an access review acts on.
 */
export const UnlistedReach = {
  /**
   * No grant names this resource and its type resolves OPEN, so every member of
   * the organization reaches it. The empty list is not "nobody".
   */
  ORGANIZATION: "organization",
  /**
   * At least one grant on the wildcard resource id (`*`) reaches every resource
   * of this type, including this one. Those rows are not listed here — they name
   * no resource, so they belong to the type's own surface — but their existence
   * is what stops an empty list meaning "nobody".
   */
  TYPE_WIDE_GRANT: "type_wide_grant",
  /** Only the grants listed reach this resource. An empty list here IS "nobody". */
  NOBODY: "nobody"
} as const;

export type UnlistedReach = (typeof UnlistedReach)[keyof typeof UnlistedReach];

export const UNLISTED_REACH_VALUES = Object.values(UnlistedReach) as [
  UnlistedReach,
  ...UnlistedReach[]
];

/**
 * Per-resource-type visibility override map.
 * Keys are ResourceType values. Missing keys fall back to the org's global defaultResourceVisibility.
 * Stored as JsonB on Organization.resourceVisibilityByType.
 */
export const ResourceVisibilityConfigSchema = z.partialRecord(
  z.enum(RESOURCE_TYPES),
  z.enum(RESOURCE_VISIBILITY_VALUES)
);

export type ResourceVisibilityConfig = z.infer<typeof ResourceVisibilityConfigSchema>;

/**
 * Resource types PINNED to a visibility, regardless of anything the org sets.
 *
 * The org-level default exists so a team can decide whether *their* content is
 * shared-by-default. It is the wrong knob for a resource reachable from the
 * public internet: an org that never thought about the setting would be
 * publishing to the world by omission.
 *
 * **This was a default with an override above it, and PR #1990's review showed
 * that could not work.** Honouring an OPEN override meant automatic
 * `ORGANIZATION → EDITOR` rows that outlived the setting, so a flip back to
 * CLOSED left apps admitting the whole org under a CLOSED label. Suppressing
 * those rows instead left the override inert, because `openWhenUngranted` only
 * decides resources with NO grant rows and creation always writes the owner row.
 * Unsafe in one direction, meaningless in the other.
 *
 * So it is a pin. "Everyone in my org can open this app" stays sayable — as an
 * explicit org grant on that app, which someone chose and an audit can
 * attribute, which is the right amount of friction for something on the public
 * internet.
 *
 * `access_card` is pinned on the same argument reached from the other side. A
 * card is a scoped delegation of a credential, and the caller that redeems one
 * is a `PublicApiKey` rather than a person. `ResourcePermissionGuard` admits
 * every API-key request by explicit product decision and defers per-key scoping
 * to these grant rows, so an org that never opened the visibility setting would
 * be delegating every credential to every key it owns, by omission. The org
 * default is the wrong knob for that too.
 *
 * **The exemption below rests on a condition, and for `access_card` that
 * condition has to be established rather than assumed.** `honorOrgGrants` stays
 * true under CLOSED for a pinned type because such a type never collected an
 * automatic `ORGANIZATION → EDITOR` row — `initializeResourcePermissions` writes
 * one only when `openWhenUngranted` is true. That holds for `vibe_app` by birth.
 * It does not hold for `access_card`, which was created ungated in OPEN orgs and
 * therefore already carries those rows; every surviving one is honoured here as
 * if an operator had written it deliberately. They are removed by migration, and
 * the pin is what stops new ones being written.
 *
 * `track` satisfies that condition in the STRONGEST available form, and it is
 * the one member here that can be checked rather than argued. `access_card`
 * needed a migration because it predates its own pin; `vibe_app` holds by birth.
 * `track` was not a {@link ResourceType} member at all until it was pinned in the
 * same change, so no `ResourcePermission` row naming it can exist in any
 * database — not "should not", cannot: `AssertPermissionResourceService` is the
 * only mint of a verified resource, and it could not name a type the union did
 * not carry. `SELECT count(*) FROM "ResourcePermission" WHERE "resourceType" =
 * 'track'` is 0 by construction, and it is 0 in lowercase — `resourceType` is
 * stored as the const's VALUE, so a `= 'TRACK'` census returns 0 for the wrong
 * reason and reads identically.
 *
 * 🚨 THE PIN DECIDES WHAT A CHECK ANSWERS; IT DOES NOT MAKE A CHECK RUN. No
 * tracks route evaluates a permission today, so pinning `track` changes no
 * caller's reach in this change. What it does is fix the DEFAULT the first
 * gating lane inherits, and it has to be here rather than there: a type that
 * ships unpinned starts collecting `Organization.defaultResourceVisibility`
 * (`"open"`) as its answer, and the automatic `ORGANIZATION → EDITOR` rows that
 * an OPEN create path writes are exactly what made this a pin instead of a
 * default in the first place.
 */
export const SYSTEM_DEFAULT_RESOURCE_VISIBILITY: Partial<Record<ResourceType, ResourceVisibility>> =
  {
    [ResourceType.VIBE_APP]: ResourceVisibility.CLOSED,
    [ResourceType.ACCESS_CARD]: ResourceVisibility.CLOSED,
    [ResourceType.TRACK]: ResourceVisibility.CLOSED
  };

/** True when no organisation setting can move this type — it carries a system pin. */
export function isResourceVisibilityPinned(resourceType: ResourceType): boolean {
  return SYSTEM_DEFAULT_RESOURCE_VISIBILITY[resourceType] !== undefined;
}

/**
 * The resource types an organisation's per-type override can actually move.
 *
 * DERIVED from the pin map rather than listed, so pinning a new type narrows
 * every schema built on this in the same edit. A hand-written list would let a
 * write endpoint keep accepting a type the enforcement path had started
 * ignoring — and an override that is stored and never honoured is worse than a
 * refusal, because the stored value then reads as the answer.
 */
export const OVERRIDABLE_RESOURCE_TYPES = RESOURCE_TYPES.filter(
  (resourceType) => !isResourceVisibilityPinned(resourceType)
) as [ResourceType, ...ResourceType[]];

// ============================================================================
// Resource Access Policy
// ============================================================================

/**
 * What a resource type's visibility setting actually decides.
 *
 * This used to be one boolean, `isDefaultOpen`, and it silently answered two
 * independent questions at once:
 *
 *   1. a resource with NO grant rows — visible, or denied?
 *   2. do ORGANIZATION-subject grant rows count?
 *
 * OPEN answered yes/yes and CLOSED answered no/no, so the third combination —
 * **deny when ungranted, but honour an org grant the operator wrote on purpose**
 * — could not be expressed at all. That is exactly the combination an
 * internet-facing resource needs, and reaching it by running Vibe apps in OPEN
 * mode would mean an app whose last grant row was deleted silently becomes
 * world-readable. Fail-open is not available to us here.
 *
 * Naming the two questions separately keeps every existing type bit-identical
 * (OPEN → `true/true`, CLOSED → `false/false`) and makes the missing cell
 * reachable. They travel as ONE object because they must be decided together:
 * two booleans threaded through three call sites is how they drift.
 */
export interface ResourceAccessPolicy {
  /** No grant rows at all ⇒ visible to the whole org? */
  readonly openWhenUngranted: boolean;
  /** Do explicit `ORGANIZATION`-subject grants grant access? */
  readonly honorOrgGrants: boolean;
}

/**
 * Resolve the access policy for a resource type from its effective visibility.
 *
 * CLOSED ignores org grants for the types that get one automatically: an org
 * that flips OPEN → CLOSED still carries the `organization → editor` rows
 * `initializeResourcePermissions` wrote while it was OPEN, and honouring those
 * would leave the org wide open under a CLOSED label.
 *
 * A type with a SYSTEM entry (`vibe_app`) is exempt, and honours a deliberate org
 * grant even while CLOSED — otherwise "everyone in my org can open this app"
 * would be unexpressible. That exemption is sound only because such a type can
 * never have been OPEN: `resolveVisibility` PINS it to its system entry rather
 * than letting a per-type override move it, so no automatic org row was ever
 * written for one and an org row on it can only be deliberate.
 *
 * The pin is what carries that invariant. Weakening it to a default — an
 * override the org can set — reintroduces exactly the leftover-row hole this
 * exemption would then be walking into.
 */
const POLICY_BY_VISIBILITY: Record<
  ResourceVisibility,
  (resourceType: ResourceType) => ResourceAccessPolicy
> = {
  [ResourceVisibility.OPEN]: () => ({ openWhenUngranted: true, honorOrgGrants: true }),
  [ResourceVisibility.CLOSED]: (resourceType) => ({
    openWhenUngranted: false,
    honorOrgGrants: SYSTEM_DEFAULT_RESOURCE_VISIBILITY[resourceType] === ResourceVisibility.CLOSED
  })
};

export function resolveResourceAccessPolicy(
  resourceType: ResourceType,
  visibility: ResourceVisibility
): ResourceAccessPolicy {
  // A total map, not an if/else: every access path in the codebase derives its
  // behaviour from the policy this returns, so a new ResourceVisibility value
  // must fail the build here rather than fall through to the CLOSED branch and
  // pick up its answer to both questions by accident.
  return POLICY_BY_VISIBILITY[visibility](resourceType);
}

// ============================================================================
// Visibility (derived UI concept)
// ============================================================================

/**
 * Human-readable visibility derived from permission records.
 * - `organization` — org-wide subject exists → everyone in org has access
 * - `private`      — only specific users/groups have access
 */
export const Visibility = {
  ORGANIZATION: "organization",
  PRIVATE: "private"
} as const;

export type Visibility = (typeof Visibility)[keyof typeof Visibility];

export const VISIBILITY_TYPES = Object.values(Visibility) as [Visibility, ...Visibility[]];

// ============================================================================
// Wildcard Resource ID
// ============================================================================

/** Sentinel value used when a grant applies to ALL resources of a given type (wildcard grant). */
export const WILDCARD_RESOURCE_ID = "*" as const;
